import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Page from '../../../../types/shared/Page';
import Studio from '../../../../types/studio/Studio';
import StudioWriteDTO from '../../../../types/studio/StudioWriteDTO';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { EnumLabelPipe } from '../../pipes/enum-label.pipe';
import { ExportService } from '../../services/export.service';
import { StudioService } from '../../services/studio.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { CreateStudioDialogComponent } from '../shared/studios/create-studio-dialog/create-studio-dialog.component';
import { UpdateStudioDialogComponent } from '../shared/studios/update-studio-dialog/update-studio-dialog.component';

@Component({
    selector: 'app-studios',
    templateUrl: './studios.component.html',
    styleUrls: [ './studios.component.scss' ],
    imports: [
        FormsModule,
        TableModule,
        RouterLink,
        PageHeaderComponent,
        IftaLabelModule,
        IconField,
        InputIcon,
        InputText,
        Button,
        CreateStudioDialogComponent,
        UpdateStudioDialogComponent,
        CreateStudioDialogComponent,
        UpdateStudioDialogComponent,
        Tag,
        IsAdminDirective,
        Tooltip,
        EnumLabelPipe
    ]
})
export class StudiosComponent implements OnInit {
    public search = '';
    public studios: Studio[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateDialog = false;
    public displayUpdateDialog = false;
    public selectedStudio: StudioWriteDTO = new StudioWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(
        private studioService: StudioService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private exportService: ExportService
    ) {
    }

    public ngOnInit(): void {
        this.loadStudios();

        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadStudios();
        });
    }

    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadStudios();
    }

    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    public editStudio(studio: Studio): void {
        this.selectedStudio = new StudioWriteDTO(studio);
        this.selectedStudio.dealershipId = studio?.dealership?.id;
        this.displayUpdateDialog = true;
    }

    public deleteStudio(studio: Studio): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the studio ' + studio.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.studioService.deleteStudio(studio.id).then(() => {
                    this.loadStudios();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Studio deleted',
                        life: 3000,
                    });
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    public openCreateDialog(): void {
        this.displayCreateDialog = true;
    }

    public reloadStudios(): void {
        this.loadStudios();
    }

    public exportStudios(): void {
        this.exportService.exportStudios().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Studios exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    private loadStudios(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.studioService
            .getStudios(search, this.currentPage, size)
            .then((result: Page<Studio>) => {
                this.studios = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
